import type { Href } from 'expo-router';

/**
 * Auth-flow destinations make no sense as a post-login redirect: the
 * (auth) layout immediately bounces authed users, so the param would
 * just be lost on that hop. Crafted links could also use them to force
 * a redirect flicker. Fall back to home instead.
 *
 * Onboarding is excluded for the opposite reason: the root router sends
 * users there itself when their profile is incomplete, and nothing
 * bounces a completed user away. Signing out from the onboarding
 * screen's "Already have an account?" link would otherwise carry
 * `/name` through login and drop the real account owner on an empty
 * profile form.
 */
const AUTH_PATH_PREFIXES = ['/login', '/verify', '/signup', '/(auth)', '/name', '/(onboarding)'];

/**
 * Validate a `?redirect=` param before navigating to it post-login.
 * Only same-app absolute paths are allowed — anything else (external
 * URLs, protocol-relative `//evil.com`, backslash variants `/\evil.com`,
 * relative paths, array params, auth-flow paths) falls back to `/`.
 * Keeps the login flow from becoming an open redirect.
 *
 * The cast to Href is safe: typed routes only guard literals at
 * compile time, and an unknown-but-internal path lands on +not-found
 * rather than anywhere dangerous.
 */
export function safeInternalPath(value: string | string[] | undefined): Href {
  if (typeof value !== 'string') return '/';
  if (!value.startsWith('/') || value.startsWith('//')) return '/';
  if (value.includes('\\')) return '/';
  if (AUTH_PATH_PREFIXES.some((prefix) => value.startsWith(prefix))) return '/';
  return value as Href;
}
